import path, { posix, win32, isAbsolute } from 'path';
import fs, { rmSync } from 'fs';
import {
  Assert,
  Dependence,
  DependenceParent,
  DependenceWhat,
  Graph,
  QueueInfo
} from '..';
import * as parser from '@babel/parser';

export type NormalizePath = (filename: string) => string;

const normalizePath: NormalizePath = function normalizePath(filename: string) {
  return filename.split(win32.sep).join(posix.sep);
};

function getAbsolutePath(filePath: string) {
  if (isAbsolute(filePath)) return normalizePath(filePath);
  return normalizePath(path.resolve(process.cwd(), filePath));
}

function writeFileSync(directory: string, filename: string, data: string) {
  if (!fs.existsSync(directory)) {
    fs.mkdirSync(directory, { recursive: true });
  }
  const filePath = path.join(directory, filename);
  fs.writeFileSync(filePath, data);
}

function mergeWhat(
  fromWhat: Array<DependenceWhat>,
  toWhat: Array<DependenceWhat>
) {
  const fMap = new Map();
  fromWhat.forEach(what => {
    fMap.set(what.binding, what.type);
  });
  for (let index = 0; index < toWhat.length; index++) {
    const what = toWhat[index];
    if (fMap.has(what.binding) && fMap.get(what.binding) === what.type) {
      continue;
    }
    fromWhat.push(what);
  }
  return fromWhat;
}

function printCallStack() {
  try {
    throw new Error();
  } catch (e) {
    console.log(e.stack());
  }
}

function cleanResource(filePath: string, recursive = true) {
  if (!fs.existsSync(filePath)) return false;
  const stat = fs.statSync(filePath);
  if (stat.isFile()) {
    fs.unlinkSync(filePath);
    return true;
  } else if (stat.isDirectory()) {
    try {
      rmSync(filePath, { recursive });
      return true;
    } catch (error) {
      throw new Error(
        `Failed to delete a directory. path: ${filePath}.  stack: ${printCallStack()}`
      );
    }
  } else {
    return true;
  }
}

function extraSopeCode(code) {
  const reg = /\{([\s\S]*?)\}/g;
  let match;
  let jsCode = '';
  let codeIndex = 1;
  // 提取所有动作部分
  while ((match = reg.exec(code)) !== null) {
    const actionCode = match[1];
    jsCode += `// Action ${codeIndex}\n`;
    jsCode += `${actionCode.trim()}\n\n`;
    codeIndex++;
  }
  return jsCode;
}

function parseCode(code: string) {
  const ast = parser.parse(code, {
    sourceType: 'module',
    plugins: ['jsx', 'typescript', 'decorators-legacy']
  });
  return ast;
}

function createAssertMap(graphs: Graph) {
  const map: Map<string, QueueInfo> = new Map();
  if (!graphs.length) return map;
  graphs.forEach(({ filePath }, index) => map.set(filePath, { index }));
  return map;
}

function sortGraph(graphs: Graph) {
  if (!graphs.length) return [];
  const asserts: Graph = [];
  for (let index = 0; index < graphs.length; index++) {
    const graph = graphs[index];
    if (!graph.parent) {
      asserts.push(graph);
      continue;
    }
    const deps = graph.dependencies;
    if (!deps || !deps.length) {
      asserts.push(graph);
      continue;
    } else {
      insertGraph(graph, deps);
      continue;
    }
  }
  function insertGraph(graph: Assert, deps: Array<Dependence>) {
    let topDepIndex = asserts.length;
    const assertMap = createAssertMap(asserts);
    deps.forEach(dep => {
      const { moduleAbsolutePath } = dep;
      let depIndex: QueueInfo | undefined;
      if ((depIndex = assertMap?.get(moduleAbsolutePath))) {
        topDepIndex =
          topDepIndex > depIndex.index ? depIndex.index : topDepIndex;
      }
    });
    if (topDepIndex < 0) {
      asserts.push(graph);
      return topDepIndex;
    }
    asserts.splice(topDepIndex, 0, graph);
    return topDepIndex;
  }
  // 移动后，parent 改变。
  sortDependence(asserts);
  return asserts;
}

function sortDependence(asserts: Graph) {
  const assertMap: Map<string, number> = new Map();
  asserts.forEach((assert, index) => assertMap.set(assert.filePath, index));
  for (let len = asserts.length - 1; len >= 0; len--) {
    const assert = asserts[len];
    const parent = assert.parent;
    if (!parent || !parent.length) continue;
    updateParent(parent, assert, len);
  }
  function updateParent(
    parents: Array<DependenceParent>,
    assert: Assert,
    assertIndex: number
  ) {
    for (let index = 0; index < parents.length; index++) {
      const parent: DependenceParent = parents[index];
      const parentIndex = assertMap.get(parent.parentPath);
      if (parentIndex === undefined) {
        throw new Error(
          `update parent Index fail,parentPath:${parent.parentPath},curPath:${
            assert.filePath
          },step:${printCallStack()}`
        );
      }
      parent.index = parentIndex;
      updateParentDependence(
        asserts[parentIndex],
        assert.filePath,
        assertIndex
      );
    }
  }
  function updateParentDependence(
    parentAssert: Assert,
    childAssertFilePath,
    childAssertIndex: number
  ) {
    const deps = parentAssert.dependencies;
    const depsMap = parentAssert.dependenciesMap;
    if (!deps.length) {
      // todo... add log
    }
    const depIndex = depsMap.get(childAssertFilePath);
    if (depIndex === undefined) {
      // todo... add log
      return;
    }

    const dep = deps[depIndex];
    if (!dep) {
      // tree shake 掉了。 import引入但是没有使用
      // add log
      return;
    }

    dep.moduleIndex = childAssertIndex;
  }
}

export {
  sortGraph,
  createAssertMap,
  parseCode,
  extraSopeCode,
  cleanResource,
  getAbsolutePath,
  mergeWhat,
  printCallStack,
  writeFileSync,
  normalizePath
};
